import { defineStore } from 'pinia'
import { useRouteQuery } from '@vueuse/router'
import * as Sentry from '@sentry/vue'
import { useAuthStore } from '~/stores/auth'
import { type SignUpData, type UserWithOrganizations } from '~/types/User'
import type { Candidate } from '~/types/Candidate'
import type { Unsaved } from '~/types/Unsaved'
import { NetworkError } from '~/errors/NetworkError'
import { UserService } from '~/services/api/user/user'
import { SuperUserService } from '~/services/api/user/super-user'

export const useUserStore = defineStore('user', () => {
  const { addToast } = useToastNotifications()
  const userService = new UserService()
  const router = useRouter()
  const route = useRoute()
  // OTHER STORES =============================================================
  const authStore = useAuthStore()

  // STATE ====================================================================

  const user = ref<UserWithOrganizations | null>(null)

  const organizations = computed(() => user.value?.organizations ?? [])

  // Flattened list of every workspace the user is a member of across all of
  // their organizations. The single place organization grouping is collapsed for the
  // current flat-list UI.
  const workspaces = computed(() =>
    organizations.value.flatMap((organization) => organization.workspaces),
  )

  const currentAuthor = computed(() => ({
    firstName: user.value?.firstName,
    lastName: user.value?.lastName,
    user: user.value?.id,
  }))

  const isUserEnrolled = computed(() => user.value !== null)

  const isSuperUser = computed(() => user.value?.isSuper ?? false)

  // ! For some reason, here and only here, useRouteQuery errors unless we pass in the router and route.
  const organizationInParams = useRouteQuery('organization', undefined, {
    router,
    route,
  })

  const userCandidateData = computed((): Unsaved<Candidate> | null => {
    if (!user.value) return null

    return {
      // The id keeps the identifier unique for an account with no email.
      identifier: user.value.email ?? user.value.id,
      firstName: user.value.firstName,
      lastName: user.value.lastName,
      organization:
        organizationInParams.value &&
        typeof organizationInParams.value === 'string'
          ? organizationInParams.value
          : 'Traverse Studio',
    }
  })

  watch(
    () => user.value?.id,
    (newUserId, oldUserId) => {
      if (newUserId === oldUserId) return

      if (!newUserId) {
        Sentry.setUser(null)
        return
      }

      Sentry.setUser({
        id: newUserId,
      })
    },
    {
      immediate: true,
    },
  )

  // CRUD =====================================================================

  async function completeSignUp(userData: SignUpData, userId: string) {
    const { data, error } = await userService.updateUserById(userId, userData)

    if (error !== null) {
      return { data, error }
    }

    await $initialise()

    return { data, error }
  }

  function getIsUserInWorkspace(workspaceId: string) {
    if (!user.value) return false

    return workspaces.value.some((workspace) => workspace.id === workspaceId)
  }

  function getIsUserInOrganization(organizationId: string) {
    if (!user.value) return false

    return organizations.value.some(
      (organization) => organization.id === organizationId,
    )
  }

  /** Resolve the organization slug for a workspace the user belongs to. */
  function getOrganizationSlugForWorkspace(workspaceId: string): string | null {
    for (const organization of organizations.value) {
      if (
        organization.workspaces.some(
          (workspace) => workspace.id === workspaceId,
        )
      ) {
        return organization.slug
      }
    }
    return null
  }

  // META ACTIONS ========================================
  function reset() {
    user.value = null
  }

  const {
    $isInitialised,
    $isInitialising,
    $isUpdating,
    $initialise,
    $ensureInitialised,
    $reset,
    $refresh,
  } = useStoreManager(
    computed(() => authStore.authUser?.email),

    async () => {
      if (
        !authStore.isAuthenticated ||
        !authStore.authUser?.email ||
        !authStore.authServiceId
      ) {
        // showError({
        //   statusCode: 500,
        //   statusMessage: 'User could not be initialised. Please sign in again.',
        // })
        return false
      }

      const { data, error } = await userService.getMe()

      if (error !== null) {
        // GET /users/me returns 401 when the request is authenticated but there
        // is no matching (or an deactivated) app account. 404 is kept for
        // safety in case the API surfaces it that way.
        if (
          error instanceof NetworkError &&
          (error.statusCode === 401 || error.statusCode === 404)
        ) {
          showError({
            statusCode: 404,
            statusMessage:
              'You were signed in successfully, but you do not have an account on Traverse Studio. Please request an invite from a Traverse Studio admin.',
          })
          return false
        }
        showError(error)
        return false
      }

      user.value = data

      return true
    },

    reset,

    async () => {
      if (!authStore.isAuthenticated || !user.value?.id) return

      const { data, error } = await userService.getMe()

      if (error !== null) {
        addToast({
          type: 'error',
          heading: 'Error refreshing user',
          message:
            'The user could not be refreshed. If the problem persists, please try reloading the page.',
          life: 5000,
        })
        return
      }

      user.value = data
    },
  )

  // SUPER USER ACTIONS =============================================================
  const superUserService = new SuperUserService()

  async function requestAppUpdateRefresh() {
    if (!isSuperUser.value) return

    await superUserService.requestAppUpdateRefresh()
  }

  addToWindowDevTools('requestAppUpdateRefresh', requestAppUpdateRefresh)

  async function getActiveConnections(
    outputMode: 'userIds' | 'workspaceIds' | 'all' | 'pretty' = 'all',
  ) {
    if (!isSuperUser.value) return

    const { data, error } = await superUserService.getActiveConnections()

    if (error !== null) {
      console.error(error)
      return
    }

    switch (outputMode) {
      case 'userIds':
        console.info(Array.from(new Set(Object.values(data).flat())))
        break
      case 'workspaceIds':
        console.info(Array.from(new Set(Object.keys(data))))
        break
      case 'all':
        console.info(data)
        break
      case 'pretty':
        Object.keys(data).forEach((workspaceId) => {
          console.info(`Workspace ${workspaceId}:`)
          data[workspaceId].forEach((userId) => {
            console.info(`  User ${userId}`)
          })
        })
        break
    }
  }

  addToWindowDevTools('getActiveConnections', getActiveConnections)
  addToWindowDevTools('getFeatureActive', getFeatureActive)

  return {
    user,
    organizations,
    workspaces,
    currentAuthor,
    $isInitialising,
    $isUpdating,
    $isInitialised,
    isUserEnrolled,
    $ensureInitialised,
    $initialise,
    $reset,
    $refresh,
    completeSignUp,
    userCandidateData,
    getIsUserInWorkspace,
    getIsUserInOrganization,
    getOrganizationSlugForWorkspace,
    isSuperUser,
  }
})
